// People: подряды без наездов, переназначение перетаскиванием, нагрузка, порядок людей.
import { addDays, daysBetween, mondayOf, overlaps, sundayOf, today, type ISODate } from '@core/dates';
import type { Change, Row } from '@core/store/model';
import { GRAY } from '@core/people';
import { MAX_ASSIGNEES } from '../shared/data';
import { BAR_HEIGHT, VACATION_STRIP, rowHeight } from '../shared/geometry';

export type Task = Row<'task'>;
export type Assignee = Row<'task_assignee'>;
export type Person = Row<'person'>;
export type Vacation = Row<'vacation'>;

export const UNASSIGNED = '__unassigned__';

export type VacationDraft = { id: string | null; person_id: string; start_date: ISODate; end_date: ISODate; note: string };
/** Новый отпуск: неделя даты at целиком, «Vacation»; person_id пустой — спросим в форме. */
export const newVacationDraft = (person_id: string, at = today()): VacationDraft => ({ id: null, person_id, start_date: mondayOf(at), end_date: sundayOf(at), note: 'Vacation' });
export const vacationDraftOf = (v: Pick<Vacation, 'id' | 'person_id' | 'start_date' | 'end_date' | 'note'>): VacationDraft =>
  ({ id: v.id, person_id: v.person_id, start_date: v.start_date, end_date: v.end_date, note: v.note ?? '' });
export type PersonRow = { id: string; name: string; color: string; unassigned: boolean };

/** Строки экрана: люди уже в личном порядке (usePeopleOrder), без скрытых; «Only me» — только я и без строки Unassigned. */
export function peopleRows(ordered: Iterable<Pick<Person, 'id' | 'name' | 'color'>>, o: { hidden: string[]; onlyMe: boolean; myPersonId: string | null }): PersonRow[] {
  const list = [...ordered]
    .filter((p) => !o.hidden.includes(p.id))
    .filter((p) => !o.onlyMe || !o.myPersonId || p.id === o.myPersonId)
    .map((p) => ({ id: p.id, name: p.name, color: p.color, unassigned: false }));
  if (!o.onlyMe) list.push({ id: UNASSIGNED, name: 'Unassigned', color: GRAY, unassigned: true });
  return list;
}

/** Задачи по человеку: одна задача — в строке каждого исполнителя; без исполнителей — в Unassigned. Только роадмап, без бэклога. */
export function tasksByPerson(assignees: Iterable<Pick<Assignee, 'task_id' | 'person_id'>>, tasks: Map<string, Task>): Map<string, Task[]> {
  const m = new Map<string, Task[]>();
  const assigned = new Set<string>();
  for (const a of assignees) {
    const t = tasks.get(a.task_id);
    if (!t || t.in_backlog) continue;
    assigned.add(t.id);
    m.set(a.person_id, [...(m.get(a.person_id) ?? []), t]);
  }
  const un: Task[] = [];
  for (const t of tasks.values()) if (!t.in_backlog && !assigned.has(t.id)) un.push(t);
  m.set(UNASSIGNED, un);
  return m;
}

/**
 * Перетащили бар из строки одного человека в строку другого (и, возможно, на другую неделю):
 * даты сдвигаются, исходный исполнитель заменяется целевым, остальные сохраняются.
 * Если целевой уже среди исполнителей — только убираем исходного.
 */
export function planReassign(
  task: Task, assignees: Assignee[], fromPersonId: string | null, toPersonId: string | null, newStart: ISODate,
): Change[] {
  const changes: Change[] = [];
  const delta = daysBetween(task.start_date, newStart);
  if (delta !== 0) {
    const set: Record<string, unknown> = { start_date: newStart, end_date: addDays(task.end_date, delta) };
    changes.push({ table: 'task', id: task.id, set, expect_updated_at: task.updated_at });
  }
  // null — строка «Unassigned»: оттуда — добавить целевого, туда — убрать исходного.
  if (fromPersonId !== toPersonId) {
    const mine = assignees.filter((a) => a.task_id === task.id).sort((a, b) => a.position - b.position);
    const from = fromPersonId ? mine.find((a) => a.person_id === fromPersonId) : undefined;
    const already = toPersonId ? mine.some((a) => a.person_id === toPersonId) : false;
    if (!toPersonId) {
      if (from) changes.push({ table: 'task_assignee', id: from.id, delete: true });
    } else if (from && already) {
      changes.push({ table: 'task_assignee', id: from.id, delete: true });
    } else if (from) {
      changes.push({ table: 'task_assignee', id: from.id, set: { person_id: toPersonId }, expect_updated_at: from.updated_at });
    } else if (!already && mine.length < MAX_ASSIGNEES) {
      changes.push({ table: 'task_assignee', insert: { task_id: task.id, person_id: toPersonId, position: mine.length } });
    }
  }
  return changes;
}

/** Нагрузка в диапазоне: сколько задач его пересекают. */
export function countInRange(tasks: Pick<Task, 'start_date' | 'end_date' | 'in_backlog'>[], from: ISODate, to: ISODate): number {
  return tasks.filter((t) => !t.in_backlog && overlaps(t.start_date, t.end_date, from, to)).length;
}

/**
 * Высота строки человека: подряды задач плюс слот под контур (чужая цель броска или новая задача — своя строка
 * не растёт: вырастая, она накрыла бы точку цели), плюс полоса подписи отпуска над барами, если отпуска есть.
 */
export function personRowShape(p: { subRows: Iterable<number>; taskCount: number; extraSlot: boolean; hasVacations: boolean; barH: number }): { subCount: number; strip: number; height: number } {
  const subCount = Math.max(1, ...[...p.subRows].map((r) => r + 1)) + (p.extraSlot ? 1 : 0);
  const strip = p.hasVacations ? VACATION_STRIP : 0;
  return { subCount, strip, height: rowHeight(subCount, p.taskCount ? p.barH : BAR_HEIGHT) + strip };
}
